/* ============================================================================
 * ALTIORA — TrajectoryLens
 * ----------------------------------------------------------------------------
 * Composant d'accueil doctrinal pour tous les membres.
 * Consomme M1 (Compréhension) + M2 (Décision) — les 2 moteurs les plus
 * proches du vécu quotidien du membre.
 *
 * 3 états gérés :
 *  - Pas de diagnostic complété    → invitation douce à démarrer
 *  - En cours (draft/in_progress)   → invitation à reprendre où on s'est arrêté
 *  - Complété                       → narratif M1 + 1 action M2 today prioritaire
 *
 * Règle des 5 Valeurs :
 *  - Membre : voir immédiatement qui il est et quoi faire (Principe 6)
 *  - Pro : cadre commun avec ses mentorés
 *  - Institution : matérialisation concrète du contrat Trusted Intelligence™
 *  - Science : chaque conclusion reste traçable via why_fr
 *  - Modèle éco : la lentille devient le point d'ancrage produit
 * ==========================================================================*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PaywallTeaser from "@/components/PaywallTeaser";
import {
  ArrowRight, Sparkles, Compass, Zap, CheckCircle2, Wind, Info,
} from "lucide-react";

const HORIZON_ICONS = {
  today: Zap,
  this_week: Compass,
  this_quarter: Sparkles,
};

const HORIZON_LABEL_FR = {
  today: "Aujourd'hui",
  this_week: "Cette semaine",
  this_quarter: "Ce trimestre",
};

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
});

export default function TrajectoryLens({ displayName = "" }) {
  const { user } = useAuth();
  const isPaid = user?.entitlement?.is_paid === true;
  const [state, setState] = useState("loading"); // loading | none | draft | ready | paywall
  const [comprehension, setComprehension] = useState(null);
  const [decision, setDecision] = useState(null);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    (async () => {
      // Non-paid users : afficher directement le teaser (Phase A · Q2c)
      if (user && !isPaid) {
        setState("paywall");
        return;
      }
      // 1. Ping /diagnostic/me to know the status
      try {
        const { data } = await api.get("/diagnostic/me");
        const status = data?.diagnostic?.status;
        if (status !== "completed") {
          setState(status ? "draft" : "none");
          return;
        }
        // 2. Fetch M1 + M2 in parallel — gracefully handle 402 (freshly downgraded)
        const [m1, m2] = await Promise.all([
          api.get("/diagnostic/me/comprehension").catch((err) => {
            if (err?.response?.status === 402) setState("paywall");
            return null;
          }),
          api.get("/diagnostic/me/decision").catch(() => null),
        ]);
        setComprehension(m1?.data || null);
        setDecision(m2?.data || null);
        setState((prev) => (prev === "paywall" ? "paywall" : "ready"));
      } catch (err) {
        setState("none");
      }
    })();
  }, [user, isPaid]);

  if (state === "loading") {
    return (
      <div className="border border-[#E4E1D8] bg-white rounded-xl p-8 min-h-[220px] flex items-center justify-center">
        <div className="text-xs font-mono uppercase tracking-widest text-[#8B8B8F]">
          Ouverture de votre lentille de trajectoire…
        </div>
      </div>
    );
  }

  if (state === "paywall") {
    return <PaywallTeaser capability="dossier.optimize_ai" unlockedByPlans={["ALTIORA Plus", "ALTIORA Business Pro"]} />;
  }

  if (state === "none") {
    return (
      <motion.section
        {...fadeIn(0)}
        className="border border-[#0B2545]/25 bg-gradient-to-br from-white via-[#FBF6EA]/40 to-white rounded-xl p-8 md:p-10"
        data-testid="trajectory-lens-empty"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#0B2545]/10 text-[#0B2545] flex items-center justify-center flex-shrink-0">
            <Wind className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-2">
              Diagnostic IMES™ · point de départ
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight">
              {displayName ? `Bienvenue, ${displayName}.` : "Bienvenue."}
            </h2>
            <p className="mt-3 text-sm md:text-base text-[#374151] leading-relaxed max-w-2xl">
              Avant que nous vous proposions un plan, prenons 12 minutes pour comprendre
              votre trajectoire — d&apos;où vous venez, où vous êtes, où vous pouvez aller.
              Ce n&apos;est pas un formulaire ; c&apos;est une conversation.
            </p>
            <Link
              to="/app/diagnostic"
              data-testid="trajectory-lens-start"
              className="mt-6 inline-flex items-center gap-2 bg-navy hover:bg-[#123163] text-white text-sm font-medium px-5 py-3 rounded-full transition-all hover:-translate-y-0.5"
            >
              Commencer mon diagnostic <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </motion.section>
    );
  }

  if (state === "draft") {
    return (
      <motion.section
        {...fadeIn(0)}
        className="border border-[#0B2545]/25 bg-white rounded-xl p-8"
        data-testid="trajectory-lens-draft"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Compass className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-2">
              Diagnostic · à reprendre
            </div>
            <h2 className="font-display text-xl md:text-2xl text-navy leading-tight">
              Votre conversation est en pause.
            </h2>
            <p className="mt-2 text-sm text-[#374151] max-w-2xl">
              Reprenez là où vous vous êtes arrêté(e). Vos réponses sont sauvegardées.
            </p>
            <Link
              to="/app/diagnostic"
              data-testid="trajectory-lens-resume"
              className="mt-4 inline-flex items-center gap-2 bg-navy hover:bg-[#123163] text-white text-sm font-medium px-4 py-2.5 rounded-md transition"
            >
              Reprendre <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </motion.section>
    );
  }

  // state === "ready"
  const answer1 = comprehension?.answer || {};
  const answer2 = decision?.answer || {};
  const topAction = (answer2.actions || []).find((a) => a.horizon === "today") || (answer2.actions || [])[0];
  const HorizonIcon = topAction ? HORIZON_ICONS[topAction.horizon] || Zap : Zap;
  const imes = answer1.imes;
  const grade = answer1.grade;

  return (
    <motion.section
      {...fadeIn(0)}
      className="border border-[#0B2545]/25 bg-white rounded-xl overflow-hidden"
      data-testid="trajectory-lens-ready"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 p-8 md:p-10">
        {/* ─── M1 · Compréhension ─────────────────────────── */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-3">
            Trusted Intelligence™ · M1 Compréhension
          </div>
          <motion.h2
            {...fadeIn(0.05)}
            className="font-display text-2xl md:text-3xl text-navy leading-snug"
          >
            {displayName ? `Voici où vous en êtes, ${displayName}.` : "Voici où vous en êtes."}
          </motion.h2>
          <motion.p
            {...fadeIn(0.15)}
            className="mt-4 text-base text-[#374151] leading-relaxed max-w-2xl"
            data-testid="lens-narrative"
          >
            {answer1.narrative_fr || "Analyse en cours…"}
          </motion.p>

          {/* Explainability toggle (Principe 6) */}
          {comprehension?.why_fr && (
            <div className="mt-4">
              <button
                onClick={() => setShowWhy((v) => !v)}
                data-testid="lens-explain-toggle"
                className="inline-flex items-center gap-1.5 text-xs text-navy hover:underline"
              >
                <Info className="h-3 w-3" />
                {showWhy ? "Masquer l'explication" : "Pourquoi cette lecture ?"}
              </button>
              <AnimatePresence>
                {showWhy && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto", transition: { duration: 0.35 } }}
                    exit={{ opacity: 0, height: 0, transition: { duration: 0.2 } }}
                    className="mt-3 p-4 bg-[#F1EFE9] rounded-lg border border-[#E4E1D8] text-xs text-[#374151] space-y-1.5"
                  >
                    <div><strong className="text-navy">Pourquoi :</strong> {comprehension.why_fr}</div>
                    <div><strong className="text-navy">Confiance :</strong> {Math.round((comprehension.confidence || 0) * 100)}%</div>
                    <div><strong className="text-navy">Sources :</strong> {(comprehension.sources_fr || []).join(" · ")}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ─── IMES score cinématique ─────────────────────── */}
        {typeof imes === "number" && (
          <motion.div
            {...fadeIn(0.1)}
            className="lg:border-l lg:border-[#E4E1D8] lg:pl-8 flex flex-col justify-center items-start lg:items-center"
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] mb-1">IMES™</div>
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] } }}
              className="font-display text-5xl md:text-6xl text-navy leading-none tabular-nums"
              data-testid="lens-imes"
            >
              {imes.toFixed(1)}
            </motion.div>
            {grade && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.7 } }}
                className="mt-2 inline-block px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#F1EFE9] text-navy rounded"
              >
                grade {grade}
              </motion.div>
            )}
            <Link
              to="/app/diagnostic/report"
              data-testid="lens-open-report"
              className="mt-4 inline-flex items-center gap-1.5 text-xs text-navy hover:underline"
            >
              Voir le rapport complet <ArrowRight className="h-3 w-3" />
            </Link>
          </motion.div>
        )}
      </div>

      {/* ─── M2 · Décision · Prochaine action prioritaire ─── */}
      {topAction && (
        <motion.div
          {...fadeIn(0.25)}
          className="border-t border-[#E4E1D8] bg-[#F8F7F3] px-8 md:px-10 py-6 flex items-center gap-4 flex-wrap"
          data-testid="lens-next-action"
        >
          <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <HorizonIcon className="h-4.5 w-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-0.5">
              M2 · Prochaine action · {HORIZON_LABEL_FR[topAction.horizon] || topAction.horizon}
            </div>
            <div className="font-medium text-navy text-base leading-snug">
              {topAction.title_fr}
            </div>
            {topAction.impact_fr && (
              <div className="mt-1 text-xs text-[#5B5B62] leading-relaxed max-w-2xl">
                {topAction.impact_fr}
              </div>
            )}
          </div>
          {topAction.duration_fr && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] whitespace-nowrap">
              ~{topAction.duration_fr}
            </div>
          )}
          <Link
            to="/app/priorities"
            data-testid="lens-open-priorities"
            className="inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white text-xs font-medium px-3 py-1.5 rounded-md transition"
          >
            Voir mes priorités <ArrowRight className="h-3 w-3" />
          </Link>
        </motion.div>
      )}
    </motion.section>
  );
}
